import {
  Flex,
  Input,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
} from "@chakra-ui/react";
import { Button } from "@chakra-ui/react";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";
import { useCallback, useRef, useState } from "react";
import { CiExport } from "react-icons/ci";
import DatePicker from "react-datepicker";
import { startOfMonth, endOfMonth } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export function useExcelTemplate({
  columnDef,
  moduleName,
  getDynamicColumns,
  templateName,
  firstColumnPrefix,
  onImport,
  secondColumnPrefix,
  modId,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [dates, setDates] = useState([null, null]);
  const [isMonth, setIsMonth] = useState(false);
  const [startDate, endDate] = dates;

  // Evaluates to true only when the user has fully chosen both start and end dates
  const isRangeSelected = startDate !== null && endDate !== null;

  const handleExportWithDates = async (e) => {
    if (!isRangeSelected) return;

    const dateFilters = {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
    };

    await handleExport(e, dateFilters);
    handleClose();
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const fileInputRef = useRef(null);

  const handleClick = (event) => {
    if (!event) return;

    // Save the clicked element to position the popover
    setAnchorEl(event.currentTarget);
  };

  const resolveNestedOrLiteralValue = (obj, pathOrLiteral) => {
    if (!pathOrLiteral) return "";
    if (!obj || typeof pathOrLiteral !== "string") return pathOrLiteral;

    if (!pathOrLiteral.includes(".") && !pathOrLiteral.includes("?")) {
      return obj[pathOrLiteral] !== undefined
        ? obj[pathOrLiteral]
        : pathOrLiteral;
    }

    const cleanPath = pathOrLiteral.replace(/\?/g, "");
    const value = cleanPath
      .split(".")
      .reduce(
        (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
        obj,
      );

    return value !== undefined ? value : "";
  };

  const handleExport = useCallback(
    async (e, dateFilters = {}) => {
      e.stopPropagation();
      try {
        const {
          mainCols,
          childSheets = {},
          records = [],
        } = getDynamicColumns
          ? await getDynamicColumns(dateFilters)
          : { mainCols: columnDef, childSheets: {}, records: [] };
        console.log("records", records);

        const allCols = [...mainCols];
        const workbook = new Workbook();
        const mainSheet = workbook.addWorksheet("main");

        // Metadata Sheet Generation
        const metaSheet = workbook.addWorksheet("__meta");
        metaSheet.state = "hidden";

        const dropdownLookupSheet = workbook.addWorksheet("__dropdowns");
        dropdownLookupSheet.state = "hidden";
        const dropdownReferences = {};

        metaSheet.addRow(["templateName", templateName]);
        metaSheet.addRow(["modId", modId]);
        metaSheet.addRow(["createdAt", new Date().toISOString()]);
        metaSheet.addRow(["version", "1.0"]);
        metaSheet.addRow([
          "hasChildSheets",
          Object.keys(childSheets).length > 0,
        ]);
        metaSheet.addRow([
          "mainColumns",
          JSON.stringify(allCols.map((c) => c.name)),
        ]);
        metaSheet.addRow([
          "childSheets",
          JSON.stringify(
            Object.keys(childSheets).map((key) => ({
              name: key,
              title: childSheets[key]?.sheetTitle,
              columns: childSheets[key]?.cols?.map((c) => c.name) || [],
              firstColumn: childSheets[key]?.firstColumn?.name,
            })),
          ),
        ]);

        const ddMap = {};
        const processForDD = (cols) => {
          cols.forEach((col) => {
            if (col.options?.length) {
              ddMap[col.name] = col.options.map((o) => o.label);
            }
          });
        };

        processForDD(mainCols);
        Object.values(childSheets).forEach((sheet) => processForDD(sheet.cols));

        let lookupColumnIndex = 1;
        Object.entries(ddMap).forEach(([fieldName, values]) => {
          if (!values?.length) return;

          const columnLetter =
            dropdownLookupSheet.getColumn(lookupColumnIndex).letter;
          values.forEach((value, rowIndex) => {
            dropdownLookupSheet.getCell(rowIndex + 1, lookupColumnIndex).value =
              value;
          });

          dropdownReferences[fieldName] = {
            columnLetter,
            lastRow: values.length,
          };
          lookupColumnIndex++;
        });

        mainSheet.columns = allCols.map((col) => ({
          header: col.columnName,
          key: col.name,
          width: 25,
        }));

        mainSheet.getRow(1).eachCell((cell, colNumber) => {
          const col = allCols[colNumber - 1];
          if (col?.required) {
            cell.font = { color: { argb: "FFFF0000" }, bold: true };
          } else {
            cell.font = { bold: true };
          }
        });

        const keyRow = mainSheet.addRow(allCols.map((c) => c.name));
        keyRow.hidden = true;

        const sampleRow = {};
        allCols.forEach((col, index) => {
          let value;
          if (col.name === "weekOff") return;
          if (col.options?.length) value = col.options[0].label;
          else if (col.type === "date" || col.type === "datePicker")
            value = new Date();
          else if (col?.ctrl === "phoneNo") value = "919908876654";
          else if (col?.type === "email") value = "sample@gmail.com";
          else if (col.sampleVal) value = col.sampleVal;
          else value = "Sample";

          if (index === 0) sampleRow[col.name] = firstColumnPrefix ?? null;
          else if (index === 1)
            sampleRow[col.name] = secondColumnPrefix ?? value;
          else sampleRow[col.name] = value;
        });

        // Populate row items cleanly
        if (records && records.length > 0) {
          records.forEach((record) => {
            const rowData = {};
            allCols.forEach((col, index) => {
              let cellValue = record[col.name];

              // Automatically append prefixes if assigned
              if (index === 0 && firstColumnPrefix) {
                const prefixValue = resolveNestedOrLiteralValue(
                  record,
                  firstColumnPrefix,
                );
                if (
                  prefixValue &&
                  cellValue !== undefined &&
                  cellValue !== null &&
                  !String(cellValue).startsWith(prefixValue)
                ) {
                  cellValue = `${prefixValue}${cellValue}`;
                }
              }
              if (index === 1 && secondColumnPrefix) {
                const prefixValue = resolveNestedOrLiteralValue(
                  record,
                  secondColumnPrefix,
                );
                if (
                  prefixValue &&
                  cellValue !== undefined &&
                  cellValue !== null &&
                  !String(cellValue).startsWith(prefixValue)
                ) {
                  cellValue = `${prefixValue}${cellValue}`;
                }
              }

              rowData[col.name] = cellValue !== undefined ? cellValue : null;
            });
            mainSheet.addRow(rowData);
          });
        } else {
          mainSheet.addRow(sampleRow);
        }

        // Apply dynamic drop-down constraints down column matrices
        allCols.forEach((col, colIndex) => {
          const dropdownRef = dropdownReferences[col.name];
          if (!dropdownRef) return;

          const colLetter = mainSheet.getColumn(colIndex + 1).letter;
          for (let row = 3; row <= 1000; row++) {
            mainSheet.getCell(`${colLetter}${row}`).dataValidation = {
              type: "list",
              allowBlank: true,
              formulae: [
                `'__dropdowns'!$${dropdownRef.columnLetter}$1:$${dropdownRef.columnLetter}$${dropdownRef.lastRow}`,
              ],
            };
          }
        });

        // Sub-Sheets tabs creation loop
        Object.entries(childSheets).forEach(([sheetName, valueConfig]) => {
          const sheet = workbook.addWorksheet(
            (valueConfig.sheetTitle || sheetName).slice(0, 31),
          );
          const firstCol = valueConfig.firstColumn;
          const cols = valueConfig.cols || [];
          const updatedCols = [firstCol, ...cols].filter(Boolean);

          sheet.columns = updatedCols.map((col) => ({
            header: col?.columnName,
            key: col.name,
            width: 25,
          }));

          sheet.getRow(1).eachCell((cell, colNumber) => {
            const col = updatedCols[colNumber - 1];
            if (col?.required) {
              cell.font = { color: { argb: "FFFF0000" }, bold: true };
            } else {
              cell.font = { bold: true };
            }
          });

          const childKeyRow = sheet.addRow(updatedCols.map((c) => c.name));
          childKeyRow.hidden = true;

          const childSampleRow = {};
          updatedCols.forEach((col, index) => {
            let val;
            if (col.options?.length) val = col.options[0].label;
            else if (col.type === "date" || col.type === "datePicker")
              val = new Date();
            else if (col?.ctrl === "phoneNo") val = "919908876654";
            else if (col?.type === "email") val = "sample@gmail.com";
            else if (col.sampleVal) val = col.sampleVal;
            else val = "Sample";

            if (index === 0 && firstColumnPrefix)
              childSampleRow[col.name] = `${firstColumnPrefix}`;
            else childSampleRow[col.name] = val;
          });

          if (records && records.length > 0) {
            records.forEach((record) => {
              const childDataArray = record[sheetName];
              if (Array.isArray(childDataArray)) {
                childDataArray.forEach((childItem) => {
                  const childRowData = {};
                  updatedCols.forEach((col, index) => {
                    if (index === 0 && firstCol) {
                      let parentIdValue =
                        childItem[firstCol.name] ||
                        childItem["parent"] ||
                        record["parent"] ||
                        record[firstCol.name] ||
                        (allCols[0] ? record[allCols[0].name] : "") ||
                        "";

                      childRowData[col.name] = parentIdValue;
                    } else {
                      childRowData[col.name] =
                        childItem[col.name] !== undefined
                          ? childItem[col.name]
                          : null;
                    }
                  });
                  sheet.addRow(childRowData);
                });
              }
            });
          } else {
            sheet.addRow(childSampleRow);
          }

          updatedCols.forEach((col, colIndex) => {
            if (colIndex === 0) return;
            const dropdownRef = dropdownReferences[col.name];
            if (!dropdownRef) return;

            const colLetter = sheet.getColumn(colIndex + 1).letter;
            for (let row = 3; row <= 1000; row++) {
              sheet.getCell(`${colLetter}${row}`).dataValidation = {
                type: "list",
                allowBlank: true,
                formulae: [
                  `'__dropdowns'!$${dropdownRef.columnLetter}$1:$${dropdownRef.columnLetter}$${dropdownRef.lastRow}`,
                ],
              };
            }
          });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(blob, `${moduleName}-${moment().format("DD-MM-YYYY")}.xlsx`);
      } catch (error) {
        console.error("Export failed:", error);
      }
    },
    [
      columnDef,
      getDynamicColumns,
      moduleName,
      templateName,
      firstColumnPrefix,
      secondColumnPrefix,
      modId,
    ],
  );

  const TemplateAddOn = (
    <>
      <Popover
        isOpen={open}
        onClose={handleClose}
        placement="bottom-left"
        className="main-export-popover"
      >
        <PopoverContent width="250px" className="export-popover">
          <PopoverBody>
            <DatePicker
              selected={startDate}
              onChange={(update) => {
                setDates(update);
                setIsMonth(true);
              }}
              startDate={startDate}
              endDate={endDate}
              selectsRange={true}
              isClearable={true}
              customInput={<Input placeholder="Select Date Range" />}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              onMonthChange={(date) => {
                if (!isMonth) {
                  const firstDayOfMonth = startOfMonth(date);
                  const lastDayOfMonth = endOfMonth(date);
                  setDates([firstDayOfMonth, lastDayOfMonth]);
                }
              }}
              placeholderText="Select Date Range"
              className="export-select-date"
            />
          </PopoverBody>
          <PopoverFooter>
            <Flex flexDirection="column">
              <Button
                colorScheme="blue"
                className="submit-btn"
                m={1}
                leftIcon={<CiExport />}
                onClick={handleExportWithDates}
                isDisabled={!isRangeSelected}
              >
                {`Export Data XLSX`}
              </Button>
            </Flex>
          </PopoverFooter>
        </PopoverContent>
      </Popover>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".xlsx, .xls"
        onChange={(e) => {
          onImport?.(e);
          e.target.value = "";
        }}
      />
    </>
  );

  return {
    TemplateAddOn,
    handleOpenTemplatePopover: handleClick,
  };
}

export default useExcelTemplate;
